import React, { useEffect, useRef, useState } from 'react'
import { Link, usePage } from '@inertiajs/inertia-react'
import { Inertia } from '@inertiajs/inertia'
import axios from 'axios'
import DatePicker, { registerLocale } from 'react-datepicker'
import es from 'date-fns/locale/es'
import 'react-datepicker/dist/react-datepicker.css'
import AlertDismissible from '../../../Shared/Components/AlertDismissible'
import Breadcrumbs from '../../../Shared/Components/Breadcrumbs'
import PaginationList from '../../../Shared/Components/PaginationList'
import Layout from '../../../Shared/Layout/LayoutAdmin'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Row from 'react-bootstrap/Row'
import Spinner from 'react-bootstrap/Spinner'
import SectionBadge from '../SectionBadge'
import {
    formatAnnouncementDate,
    formatAnnouncementDateTime,
    fromYmd,
    toYmd,
} from '../../../utils/announcementsPermissions'
import '../announcements.css'

registerLocale('es', es)

const cleanFilters = (filters) =>
    Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== '' && value !== null && value !== undefined))

function ReadersModal({ announcement, onHide }) {
    const [state, setState] = useState({ loading: true, readers: [], error: null })

    useEffect(() => {
        let active = true
        axios
            .get(route('announcements.manage.readers', announcement.id))
            .then(({ data }) => active && setState({ loading: false, readers: data.readers || [], error: null }))
            .catch(() => active && setState({ loading: false, readers: [], error: 'No se pudo cargar el listado.' }))
        return () => {
            active = false
        }
    }, [announcement.id])

    return (
        <Modal show onHide={onHide} centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title className="fs-4">Leído por</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="text-gray-700 fw-semibold mb-5">{announcement.title}</div>
                {state.loading ? (
                    <div className="text-center py-10">
                        <Spinner animation="border" size="sm" className="me-2" /> Cargando…
                    </div>
                ) : state.error ? (
                    <div className="text-danger">{state.error}</div>
                ) : state.readers.length === 0 ? (
                    <div className="text-center text-muted py-10">Nadie ha leído este comunicado todavía.</div>
                ) : (
                    <table className="table table-row-bordered table-row-gray-200 align-middle gs-0 gy-3 mb-0">
                        <thead>
                            <tr className="fw-bold text-muted text-uppercase fs-8">
                                <th>Usuario</th>
                                <th className="text-end">Fecha y hora</th>
                            </tr>
                        </thead>
                        <tbody className="fs-7">
                            {state.readers.map((reader) => (
                                <tr key={reader.id}>
                                    <td className="fw-semibold text-gray-800">{reader.name}</td>
                                    <td className="text-end text-gray-600">{formatAnnouncementDateTime(reader.read_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={onHide}>
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

const ManageList = ({ announcements, sections, authors, filters }) => {
    const { flash } = usePage().props
    const [search, setSearch] = useState(filters.q || '')
    const [readersFor, setReadersFor] = useState(null)
    const [toDelete, setToDelete] = useState(null)
    const [deleting, setDeleting] = useState(false)
    const firstRender = useRef(true)

    const visit = (next) =>
        Inertia.get(route('announcements.manage'), cleanFilters(next), {
            preserveState: true,
            preserveScroll: true,
            replace: true,
        })

    const applyFilters = (changes) => visit({ ...filters, ...changes })

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false
            return
        }
        const timer = setTimeout(() => {
            if (search !== (filters.q || '')) applyFilters({ q: search })
        }, 450)
        return () => clearTimeout(timer)
    }, [search])

    const destroy = () => {
        setDeleting(true)
        Inertia.delete(route('announcements.manage.destroy', toDelete.id), {
            preserveScroll: true,
            onFinish: () => {
                setDeleting(false)
                setToDelete(null)
            },
        })
    }

    const hasFilters = Boolean(filters.q || filters.section || filters.author || filters.from || filters.to)
    const rows = announcements.data || []

    return (
        <Row>
            {flash?.success && <AlertDismissible message={flash.success} type="success" />}
            {flash?.error && <AlertDismissible message={flash.error} type="danger" />}
            <span className="pb-7 pt-7">
                <Breadcrumbs currentPath="admin/comunicados/administrar" />
            </span>

            <Card className="card-flush shadow-sm mb-10">
                <Card.Header className="pt-7 flex-wrap gap-3">
                    <Card.Title className="flex-column align-items-start">
                        <span className="fw-bold text-gray-800">Administrar comunicados</span>
                        <span className="text-muted fs-7 fw-semibold mt-1">{announcements.total} comunicados</span>
                    </Card.Title>
                    <div className="card-toolbar">
                        <Link href={route('announcements.manage.create')} className="btn btn-sm btn-primary">
                            <i className="bi bi-plus-lg me-1"></i> Agregar comunicado
                        </Link>
                    </div>
                </Card.Header>
                <Card.Body className="pt-2">
                    <div className="d-flex flex-wrap align-items-center gap-3 mb-6">
                        <Form.Control
                            type="search"
                            size="sm"
                            className="form-control-solid"
                            placeholder="Buscar por título o texto…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{ maxWidth: 260 }}
                        />
                        <Form.Select
                            size="sm"
                            className="form-select-solid"
                            value={filters.section}
                            onChange={(e) => applyFilters({ section: e.target.value })}
                            style={{ maxWidth: 200 }}
                        >
                            <option value="">Todas las secciones</option>
                            {sections.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.name}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Select
                            size="sm"
                            className="form-select-solid"
                            value={filters.author}
                            onChange={(e) => applyFilters({ author: e.target.value })}
                            style={{ maxWidth: 220 }}
                        >
                            <option value="">Todos los autores</option>
                            {authors.map((a) => (
                                <option key={a.id} value={a.id}>
                                    {a.name}
                                </option>
                            ))}
                        </Form.Select>
                        <div style={{ width: 140 }}>
                            <DatePicker
                                className="form-control form-control-sm form-control-solid"
                                locale="es"
                                dateFormat="dd/MM/yyyy"
                                placeholderText="Desde"
                                selected={fromYmd(filters.from)}
                                maxDate={fromYmd(filters.to) || new Date()}
                                onChange={(date) => applyFilters({ from: toYmd(date) })}
                                isClearable
                            />
                        </div>
                        <div style={{ width: 140 }}>
                            <DatePicker
                                className="form-control form-control-sm form-control-solid"
                                locale="es"
                                dateFormat="dd/MM/yyyy"
                                placeholderText="Hasta"
                                selected={fromYmd(filters.to)}
                                minDate={fromYmd(filters.from)}
                                maxDate={new Date()}
                                onChange={(date) => applyFilters({ to: toYmd(date) })}
                                isClearable
                            />
                        </div>
                        {hasFilters && (
                            <Button
                                size="sm"
                                variant="light"
                                onClick={() => {
                                    setSearch('')
                                    visit({})
                                }}
                            >
                                Limpiar
                            </Button>
                        )}
                    </div>

                    <div className="table-responsive">
                        <table className="table table-row-bordered table-row-gray-300 align-middle gs-0 gy-4">
                            <thead>
                                <tr className="fw-bold text-muted text-uppercase fs-7 bg-light">
                                    <th className="min-w-100px ps-4 rounded-start">Fecha</th>
                                    <th className="min-w-250px">Título</th>
                                    <th className="min-w-120px">Sección</th>
                                    <th className="min-w-150px">Autor</th>
                                    <th className="min-w-80px text-center">Imágenes</th>
                                    <th className="min-w-200px text-end pe-4 rounded-end">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="fs-7">
                                {rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="text-center text-muted py-10">
                                            Sin comunicados.
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((row) => (
                                        <tr key={row.id}>
                                            <td className="ps-4 text-gray-700 text-nowrap">{formatAnnouncementDate(row.published_on)}</td>
                                            <td className="fw-semibold text-gray-800">{row.title}</td>
                                            <td>
                                                <SectionBadge section={row.section} />
                                            </td>
                                            <td className="text-gray-700">{row.author}</td>
                                            <td className="text-center text-gray-700">{row.images_count}</td>
                                            <td className="text-end pe-4 text-nowrap">
                                                <Button
                                                    size="sm"
                                                    variant="light-primary"
                                                    className="me-2"
                                                    onClick={() => setReadersFor(row)}
                                                >
                                                    <i className="bi bi-eye me-1"></i> Leído por ({row.reads_count})
                                                </Button>
                                                {row.can_delete && (
                                                    <Button
                                                        size="sm"
                                                        variant="light-danger"
                                                        className="btn-icon"
                                                        title="Eliminar"
                                                        onClick={() => setToDelete(row)}
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <PaginationList links={announcements.links} />
                </Card.Body>
            </Card>

            {readersFor && <ReadersModal announcement={readersFor} onHide={() => setReadersFor(null)} />}

            {toDelete && (
                <Modal show onHide={() => !deleting && setToDelete(null)} centered>
                    <Modal.Header closeButton={!deleting}>
                        <Modal.Title>Eliminar comunicado</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        ¿Eliminar el comunicado <strong>{toDelete.title}</strong>? Se borrarán también sus imágenes y el
                        registro de lectura. Esta acción no se puede deshacer.
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="light" disabled={deleting} onClick={() => setToDelete(null)}>
                            Cancelar
                        </Button>
                        <Button variant="danger" disabled={deleting} onClick={destroy}>
                            {deleting ? 'Eliminando…' : 'Eliminar'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </Row>
    )
}

ManageList.layout = (page) => <Layout title="Administrar comunicados">{page}</Layout>

export default ManageList
